import { useState } from "react";

import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { onboardingClient, onboardingUtils } from "@v2/trpc/routes/onboarding";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { userUtils } from "@v2/trpc/routes/user";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { extractDomainFromEmail } from "@v2/utils/email";
import { v4 as uuidv4 } from "uuid";

interface CreateProjectParams {
  shopName: string;
  workspaceId: string;
  hostingType: "shopify" | "replo";
  userEmail: string;
  workspaceName?: string;
  shouldInvalidateUser?: boolean;
  onSuccess?: (projectId: string) => void | Promise<void>;
}

interface AnalyticsEventData {
  userId: string;
  stepId: string;
  stepType: string;
  stepData: {
    shopName: string;
    [key: string]: unknown;
  };
}

const formatShopNameToSubdomain = (shopName: string): string => {
  // Normalize accents: "Café" → "Cafe"
  let slug = shopName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\d\sa-z-]/g, "") // Keep only letters, numbers, spaces, dashes
    .replace(/\s+/g, "-") // Spaces → "-"
    .replace(/-+/g, "-") // Collapse multiple "-"
    .replace(/^-+|-+$/g, "") // Trim leading/trailing "-"
    .slice(0, 63); // Max 63 chars (DNS label limit)

  // Must start and end with alphanumeric; if empty or invalid, fallback
  if (!/^[\da-z]([\da-z-]{0,61}[\da-z])?$/.test(slug)) {
    // Remove invalid start/end chars
    slug = slug.replace(/^[^\da-z]+|[^\da-z]+$/g, "");
  }

  // Still invalid or empty → fallback
  if (!slug) {
    slug = "shop";
  }

  return slug;
};

export function useOnboardingCreateProject() {
  const logEvent = useLogAnalytics();
  const { mutateAsync: createProjectAsync } =
    projectRouter.create.useMutation();
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  const createProject = async (params: CreateProjectParams) => {
    const {
      shopName,
      workspaceId,
      hostingType,
      userEmail,
      workspaceName,
      shouldInvalidateUser = true,
      onSuccess,
    } = params;
    setIsCreatingProject(true);

    let subdomain = formatShopNameToSubdomain(shopName);
    if (hostingType === "replo") {
      const availabilityData =
        await onboardingClient.checkSubdomainAvailability.query({
          subdomain,
        });
      if (!availabilityData?.available) {
        subdomain = `${subdomain}-${uuidv4().slice(0, 4)}`;
      }
    }

    const domain = extractDomainFromEmail(userEmail);

    const result = await createProjectAsync({
      name: shopName.trim(),
      workspaceId: workspaceId,
      hostingType: hostingType,
      shopDomainPrefix: hostingType === "shopify" ? null : subdomain.trim(),
      workspaceName: workspaceName,
      domain: domain,
    });

    void workspaceUtils.getUserWorkspacesList.invalidate();
    void projectUtils.listWithStats.invalidate();
    void projectUtils.listWithIdAndType.invalidate();

    if (shouldInvalidateUser) {
      void userUtils.get.invalidate();
    }

    void projectUtils.listGroupedByWorkspaceForUser.invalidate();
    void onboardingUtils.checkSubdomainAvailability.invalidate();

    if (result.project?.id) {
      await onSuccess?.(result.project.id);
    }

    setIsCreatingProject(false);
    return result;
  };

  const logOnboardingStepCompleted = (data: AnalyticsEventData) => {
    logEvent("onboarding.flow.step.completed", data);
  };

  return {
    createProject,
    isCreatingProject,
    logOnboardingStepCompleted,
  };
}
